import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { MenuItem, TextField, Typography } from "@mui/material";
import { useRecoilState, useRecoilValue } from "recoil";
import { useSnackbar } from "notistack";
import { activeWorkspaceState, authState } from "../../Recoil/RecoilState";
import { baseURL } from "../../utils/StaticVariables";

// Top-bar workspace picker. The active workspace is stored server-side per user,
// so switching here changes what every page (streams, predictions, etc.) shows.
const WorkspaceSwitcher = () => {
  const token = useRecoilValue(authState)?.token;
  const [activeWorkspace, setActiveWorkspace] = useRecoilState(activeWorkspaceState);
  const [workspaces, setWorkspaces] = useState([]);
  const [switching, setSwitching] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchWorkspaces = useCallback(() => {
    if (!token) return;
    axios
      .get(`${baseURL}workspaces/user`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setWorkspaces(data || []))
      .catch(() => setWorkspaces([]));
  }, [token]);

  useEffect(() => {
    fetchWorkspaces();
    window.addEventListener("workspaces-updated", fetchWorkspaces);
    return () => window.removeEventListener("workspaces-updated", fetchWorkspaces);
  }, [fetchWorkspaces]);

  const handleChange = async (e) => {
    const target = workspaces.find((w) => (w.workspace_id || w.id) === e.target.value);
    const targetId = target?.workspace_id || target?.id;
    const currentId = activeWorkspace?.workspace_id || activeWorkspace?.id;
    if (!target || targetId === currentId) return;
    setSwitching(true);
    try {
      await axios.post(
        `${baseURL}workspaces/${targetId}/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveWorkspace({
        workspace_id: targetId,
        name: target.name,
        member_role: target.member_role || target.role || target.user_role || "admin",
        enabled_features: target.enabled_features,
        ...target,
      });
      enqueueSnackbar(`Switched to ${target.name}`, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.detail || "Failed to switch workspace", {
        variant: "error",
      });
    } finally {
      setSwitching(false);
    }
  };

  if (!activeWorkspace && !workspaces.length) return null;

  if (workspaces.length <= 1) {
    return (
      <Typography
        noWrap
        variant="body2"
        color="text.secondary"
        sx={{
          mx: { xs: 0.5, sm: 1 },
          maxWidth: { xs: 110, sm: 180 },
          fontSize: { xs: "0.8rem", sm: "0.875rem" },
        }}
      >
        {activeWorkspace?.name || workspaces[0]?.name}
      </Typography>
    );
  }

  return (
    <TextField
      select
      size="small"
      label="Workspace"
      value={activeWorkspace?.workspace_id || activeWorkspace?.id || ""}
      onChange={handleChange}
      disabled={switching}
      sx={{
        mx: { xs: 0.5, sm: 1 },
        minWidth: { xs: 100, sm: 160 },
        maxWidth: { xs: 135, sm: 220 },
        "& .MuiSelect-select": {
          py: { xs: 0.75, sm: 1 },
          fontSize: { xs: "0.8rem", sm: "0.875rem" },
        },
      }}
    >
      {workspaces.map((w) => {
        const id = w.workspace_id || w.id;
        return (
          <MenuItem key={id} value={id}>
            <Typography noWrap fontSize={{ xs: "0.8rem", sm: "0.875rem" }}>
              {w.name}
            </Typography>
          </MenuItem>
        );
      })}
    </TextField>
  );
};

export default WorkspaceSwitcher;
